import React, { useState } from 'react';
import { FaFilter, FaMapMarkerAlt, FaBuilding } from 'react-icons/fa';

interface SitesMapFilterProps {
  projects: any[];
  onFilterChange: (filters: FilterState) => void;
}

interface FilterState {
  searchTerm: string;
  selectedProjects: string[];
  showDefaultOnly: boolean;
}

const widgetFont = { fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif' };

export const SitesMapFilter: React.FC<SitesMapFilterProps> = ({ projects, onFilterChange }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [filters, setFilters] = useState<FilterState>({
    searchTerm: '',
    selectedProjects: [],
    showDefaultOnly: false
  });

  const handleFilterChange = (newFilters: Partial<FilterState>) => {
    const updatedFilters = { ...filters, ...newFilters };
    setFilters(updatedFilters);
    onFilterChange(updatedFilters);
  };

  const toggleProject = (projectId: string) => {
    const newSelectedProjects = filters.selectedProjects.includes(projectId)
      ? filters.selectedProjects.filter(id => id !== projectId)
      : [...filters.selectedProjects, projectId];

    handleFilterChange({ selectedProjects: newSelectedProjects });
  };

  const clearFilters = () => {
    const clearedFilters = {
      searchTerm: '',
      selectedProjects: [],
      showDefaultOnly: false
    };
    setFilters(clearedFilters);
    onFilterChange(clearedFilters);
  };

  const hasActiveFilters = filters.searchTerm || filters.selectedProjects.length > 0 || filters.showDefaultOnly;

  return (
    <div className="absolute top-4 left-4 z-50 bg-white rounded-xl border border-[#eaeaea] shadow-[0_2px_8px_rgba(0,0,0,0.04)] min-w-72" style={widgetFont}>
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-[#eaeaea]">
        <div className="flex items-center gap-2">
          <FaMapMarkerAlt className="text-[#444] w-3.5 h-3.5" />
          <span className="text-[13px] font-semibold text-[#171717] tracking-[-0.01em]">Sites Map</span>
        </div>
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="p-1.5 hover:bg-[#fafafa] rounded-md transition-colors duration-150"
        >
          <FaFilter className={`w-3 h-3 text-[#888] transition-transform duration-200 ${isExpanded ? 'rotate-180' : ''}`} />
        </button>
      </div>

      {/* Filter Content */}
      {isExpanded && (
        <div className="p-4 space-y-3">
          {/* Search */}
          <div>
            <label className="block text-[11px] font-medium text-[#888] uppercase tracking-wider mb-1.5">
              Search
            </label>
            <input
              type="text"
              placeholder="Site name or address..."
              value={filters.searchTerm}
              onChange={(e) => handleFilterChange({ searchTerm: e.target.value })}
              className="w-full px-3 py-2 text-[13px] border border-[#eaeaea] rounded-lg focus:outline-none focus:ring-1 focus:ring-[#888] focus:border-[#888] bg-[#fafafa] placeholder-[#aaa] transition-colors"
              style={widgetFont}
            />
          </div>

          {/* Project Filter */}
          <div>
            <label className="block text-[11px] font-medium text-[#888] uppercase tracking-wider mb-1.5">
              Projects
            </label>
            <div className="max-h-32 overflow-y-auto space-y-0.5">
              {projects.map((project) => (
                <label key={project.id} className="flex items-center gap-2 cursor-pointer px-2 py-1.5 rounded-md hover:bg-[#fafafa] transition-colors duration-150">
                  <input
                    type="checkbox"
                    checked={filters.selectedProjects.includes(project.id)}
                    onChange={() => toggleProject(project.id)}
                    className="rounded border-[#ddd] text-[#171717] focus:ring-[#888] w-3.5 h-3.5"
                  />
                  <FaBuilding className="text-[#888] w-3 h-3 flex-shrink-0" />
                  <span className="text-[13px] text-[#444] truncate">{project.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Default Sites Only */}
          <div>
            <label className="flex items-center gap-2 cursor-pointer px-2 py-1.5 rounded-md hover:bg-[#fafafa] transition-colors duration-150">
              <input
                type="checkbox"
                checked={filters.showDefaultOnly}
                onChange={(e) => handleFilterChange({ showDefaultOnly: e.target.checked })}
                className="rounded border-[#ddd] text-[#171717] focus:ring-[#888] w-3.5 h-3.5"
              />
              <span className="text-[13px] font-medium text-[#444]">Default sites only</span>
            </label>
          </div>

          {/* Clear Filters */}
          {hasActiveFilters && (
            <button
              onClick={clearFilters}
              className="w-full px-3 py-2 text-[13px] text-[#666] border border-[#eaeaea] rounded-lg hover:bg-[#fafafa] hover:border-[#ddd] transition-all duration-150"
              style={widgetFont}
            >
              Clear Filters
            </button>
          )}
        </div>
      )}
    </div>
  );
};
